/**
 * Tarjeta de un KPI que el panel **deriva de las filas que están en pantalla**,
 * no de un datapoint del catálogo.
 *
 * Por qué existe y no uso `KpiCard`: `KpiCard` pide un `MetricValue`, y un
 * `MetricValue` pide un `Provenance` con una `source` del contrato. Un número
 * derivado no tiene fuente propia — inventarle `stripe` o `plataforma` sería
 * mentir sobre de dónde salió, y pasarle `provenance={null}` diría "fuente sin
 * declarar · cargalo en Admin", que es otra cosa. Así que la proveniencia de
 * estos números es su **derivación**, escrita al lado: qué se sumó y sobre qué
 * filas, para que cualquiera pueda recalcularlo a mano.
 *
 * (Carencia anotada del design system: `ProvenanceBadge` no tiene variante
 * `derivado`, que el prototipo sí usaba.)
 */
import type { ReactNode } from 'react'

import {
  CLASE_KPI_VALOR,
  Card,
  EmptyMetricState,
  HealthDot,
  Icon,
  Stack,
  type Severidad,
} from '../../ui'

export interface KpiDerivadoProps {
  /** Nombre del KPI. */
  titulo: string
  /** Valor ya formateado. `null` = no hay con qué calcularlo. */
  valor: string | null
  /** Cómo se calcula, en palabras: `suma de MRR de las filas con 0 instalados`. */
  derivacion: string
  /** Sobre qué se calculó, ej. `31 cuentas del período julio 2026`. */
  base: string
  /** Lectura del número en prosa, debajo del valor. */
  nota?: ReactNode
  severidad?: Severidad
  /** Acción al pie, ej. "Ver las 9 cuentas". */
  accion?: ReactNode
  /** Por qué no hay valor, para el estado vacío. */
  detalleSinDato?: string
  className?: string
}

export function KpiDerivado({
  titulo,
  valor,
  derivacion,
  base,
  nota,
  severidad,
  accion,
  detalleSinDato,
  className,
}: KpiDerivadoProps) {
  return (
    <Card
      as="section"
      ariaLabel={titulo}
      severidad={severidad}
      padding="sm"
      className={className}
    >
      <Stack gap="xs">
        <h3 className="rotulo truncate">{titulo}</h3>

        {/* El valor llega ya formateado por quien lo derivó, así que va la
            clase de `KpiValor` y no el componente, que formatea él mismo. */}
        {valor === null ? (
          <EmptyMetricState
            metricNombre={titulo}
            detalle={detalleSinDato}
            size="md"
          />
        ) : (
          <span data-testid="kpi-derivado-valor" className={CLASE_KPI_VALOR}>
            {valor}
          </span>
        )}

        {nota !== undefined ? (
          <p className="text-micro text-muted-strong">{nota}</p>
        ) : null}

        <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
          {severidad !== undefined ? (
            <HealthDot health={severidad} size="sm" />
          ) : null}
          {/* La proveniencia de un derivado es su fórmula: se publica al lado
              para que el número se pueda recalcular sin abrir la base. */}
          <span className="inline-flex max-w-full items-center gap-1 rounded border border-dashed border-n300 bg-surface px-1.5 py-0.5 text-micro font-medium leading-4 text-muted-strong">
            <Icon name="orden" size={10} />
            <span className="min-w-0 truncate">
              derivado · {derivacion} · sobre {base}
            </span>
          </span>
        </div>

        {accion !== undefined ? <div className="pt-1">{accion}</div> : null}
      </Stack>
    </Card>
  )
}
